import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  LocalHospital as SurgeryIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { RoomStatus, ROOM_STATUS_DISPLAY_NAMES } from '../../types/enums';
import { getOperatingRoomsForFacility } from '../../services/operatingRoomService';
import { selectUserFacilities } from '../../redux/slices/facilitiesSlice';
import { useUserData } from '../../hooks/useUserData';
import { surgicalCaseService } from '../../services/surgicalCaseService';
import { surgicalEquipmentService } from '../../services/surgicalEquipmentService';
import { surgicalTeamsService } from '../../services/surgicalTeamsService';

const SurgicalFacilitiesDashboard = () => {
  
  const { defaultFacility } = useUserData();
  const userFacilities = useSelector(selectUserFacilities);
  
  // Get default facility ID - prioritize user's defaultFacility, then first userFacility
  const getDefaultFacilityId = useCallback(() => {
    if (defaultFacility?.id && defaultFacility.id !== '') {
      return defaultFacility.id;
    }
    if (userFacilities && userFacilities.length > 0) {
      return userFacilities[0].id;
    }
    return '';
  }, [defaultFacility, userFacilities]);
  
  const defaultFacilityId = getDefaultFacilityId();
  
  const [stats, setStats] = useState({
    totalRooms: 0,
    availableRooms: 0,
    totalSurgeries: 0,
    completedSurgeries: 0,
    pendingSurgeries: 0,
    equipmentCount: 0,
    maintenanceDue: 0,
    teamMembers: 0,
    activeTeams: 0
  });

  const [recentSurgeries, setRecentSurgeries] = useState([]);
  const [roomStatus, setRoomStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surgeryTrends, setSurgeryTrends] = useState([]);
  const [equipmentStatus, setEquipmentStatus] = useState([]);

  // Load all dashboard data when facility changes
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!defaultFacilityId) {
        setRoomStatus([]);
        setStats(prev => ({ ...prev, totalRooms: 0, availableRooms: 0 }));
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Load all data in parallel
        const [
          operatingRooms,
          surgicalCases,
          equipmentStats,
          teamStats
        ] = await Promise.all([
          getOperatingRoomsForFacility(defaultFacilityId),
          surgicalCaseService.getSurgicalCasesForFacility(defaultFacilityId, { limit: 1000 }),
          surgicalEquipmentService.getEquipmentStats(defaultFacilityId),
          surgicalTeamsService.getTeamStats(defaultFacilityId)
        ]);
        
        // Process operating rooms
        const mappedRooms = operatingRooms.map(room => ({
          id: room.id,
          name: room.name,
          status: room.status,
          currentCase: room.currentCase,
          nextCase: room.nextCase || 'No upcoming cases',
          departmentName: room.departmentName,
          equipment: room.equipment,
          lastCleaned: room.lastCleaned,
          maintenanceDue: room.maintenanceDue
        }));
        
        setRoomStatus(mappedRooms);
        
        // Process surgical cases for recent surgeries
        const recentCases = surgicalCases
          .sort((a, b) => new Date(b.startedAt || b.createdAt) - new Date(a.startedAt || a.createdAt))
          .slice(0, 5)
          .map(case_ => ({
            id: case_.id,
            patientName: case_.patientName || case_.patient?.name || 'Unknown Patient',
            surgeryType: case_.procedureName || case_.procedureType || 'Unknown Procedure',
            room: case_.operatingRoomName || case_.operatingRoom || 'Unknown Room',
            status: case_.status || 'unknown',
            duration: case_.duration || 'Unknown',
            team: case_.teamName || case_.team?.name || 'Unknown Team',
            date: case_.startedAt || case_.createdAt
          }));
        
        setRecentSurgeries(recentCases);
        
        // Process equipment status
        if (equipmentStats) {
          const totalEquipment = equipmentStats.totalEquipment || 0;
          const operational = equipmentStats.operational || 0;
          const maintenance = equipmentStats.maintenance || 0;
          const outOfService = equipmentStats.outOfService || 0;
          
          setEquipmentStatus([
            { 
              name: 'Operational', 
              value: totalEquipment > 0 ? Math.round((operational / totalEquipment) * 100) : 0, 
              color: '#4caf50' 
            },
            { 
              name: 'Maintenance', 
              value: totalEquipment > 0 ? Math.round((maintenance / totalEquipment) * 100) : 0, 
              color: '#ff9800' 
            },
            { 
              name: 'Out of Service', 
              value: totalEquipment > 0 ? Math.round((outOfService / totalEquipment) * 100) : 0, 
              color: '#f44336' 
            }
          ]);
        }
        
        // Generate surgery trends from case data (last 6 months)
        const generateTrends = () => {
          const trends = [];
          const now = new Date();
          
          for (let i = 5; i >= 0; i--) {
            const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthName = monthDate.toLocaleString('default', { month: 'short' });
            
            const monthCases = surgicalCases.filter(case_ => {
              const caseDate = new Date(case_.startedAt || case_.createdAt);
              return caseDate.getMonth() === monthDate.getMonth() && 
                     caseDate.getFullYear() === monthDate.getFullYear();
            });
            
            // Calculate estimated revenue (this would ideally come from billing service)
            const estimatedRevenue = monthCases.length * 15000; // Placeholder calculation
            
            trends.push({
              month: monthName,
              surgeries: monthCases.length,
              revenue: estimatedRevenue
            });
          }
          
          return trends;
        };
        
        setSurgeryTrends(generateTrends());
        
        // Update stats
        const availableCount = operatingRooms.filter(room => room.status === RoomStatus.AVAILABLE).length;
        setStats({
          totalRooms: operatingRooms.length,
          availableRooms: availableCount,
          totalSurgeries: surgicalCases.length,
          completedSurgeries: surgicalCases.filter(c => c.status === 'completed').length,
          pendingSurgeries: surgicalCases.filter(c => ['scheduled', 'pre-op'].includes(c.status)).length,
          equipmentCount: equipmentStats?.totalEquipment || 0,
          maintenanceDue: equipmentStats?.maintenanceDue || 0,
          teamMembers: teamStats?.totalMembers || 0,
          activeTeams: teamStats?.activeTeams || 0
        });
        
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, [defaultFacilityId]);

  const getStatusColor = (status) => {
    switch (status) {
      case RoomStatus.AVAILABLE:
      case 'available': return '#4caf50';
      case RoomStatus.OCCUPIED:
      case 'occupied': return '#2196f3';
      case RoomStatus.MAINTENANCE:
      case 'maintenance': return '#ff9800';
      case RoomStatus.OUT_OF_SERVICE:
      case 'out_of_service': return '#f44336';
      case RoomStatus.RESERVED:
      case 'reserved': return '#9c27b0';
      case 'completed': return '#4caf50';
      case 'in-progress': return '#2196f3';
      case 'scheduled': return '#9c27b0';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case RoomStatus.AVAILABLE:
      case 'available': return <CheckCircleIcon />;
      case RoomStatus.OCCUPIED:
      case 'occupied': return <PersonIcon />;
      case RoomStatus.MAINTENANCE:
      case 'maintenance': return <WarningIcon />;
      case RoomStatus.OUT_OF_SERVICE:
      case 'out_of_service': return <ErrorIcon />;
      case RoomStatus.RESERVED:
      case 'reserved': return <ScheduleIcon />;
      case 'completed': return <CheckCircleIcon />;
      case 'in-progress': return <TimeIcon />;
      case 'scheduled': return <ScheduleIcon />;
      default: return <ErrorIcon />;
    }
  };

  const getDisplayStatus = (status) => {
    return ROOM_STATUS_DISPLAY_NAMES[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ 
            p: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #708bff15 0%, #708bff05 100%)',
            border: '1px solid #708bff30',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px #708bff20',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar sx={{ bgcolor: '#708bff', width: 48, height: 48 }}>
                <ScheduleIcon />
              </Avatar>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
              {stats.availableRooms}/{stats.totalRooms}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Operating Rooms
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Available
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ 
            p: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #4caf5015 0%, #4caf5005 100%)',
            border: '1px solid #4caf5030',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px #4caf5020',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar sx={{ bgcolor: '#4caf50', width: 48, height: 48 }}>
                <SurgeryIcon />
              </Avatar>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
              {stats.totalSurgeries}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Total Surgeries
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.completedSurgeries} completed, {stats.pendingSurgeries} pending
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ 
            p: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ff980015 0%, #ff980005 100%)',
            border: '1px solid #ff980030',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px #ff980020',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar sx={{ bgcolor: '#ff9800', width: 48, height: 48 }}>
                <WarningIcon />
              </Avatar>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
              {stats.equipmentCount}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Equipment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.maintenanceDue} need maintenance
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ 
            p: 3, 
            borderRadius: 3,
            background: 'linear-gradient(135deg, #9c27b015 0%, #9c27b005 100%)',
            border: '1px solid #9c27b030',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px #9c27b020',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Avatar sx={{ bgcolor: '#9c27b0', width: 48, height: 48 }}>
                <PersonIcon />
              </Avatar>
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
              {stats.teamMembers}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Team Members
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.activeTeams} active teams
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Room Status */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Operating Room Status
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                sx={{ borderRadius: 2 }}
              >
                Add Room
              </Button>
            </Box>
            {roomStatus.length === 0 ? (
              <Alert severity="info">
                No operating rooms found for this facility. Make sure rooms are configured with type "Operating Room".
              </Alert>
            ) : (
              <List sx={{ p: 0 }}>
                {roomStatus.map((room, index) => (
                <React.Fragment key={room.id}>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getStatusColor(room.status) }}>
                        {getStatusIcon(room.status)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {room.name}
                          </Typography>
                          <Chip
                            label={getDisplayStatus(room.status)}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(room.status),
                              color: 'white',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          {room.currentCase && (
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              Current: {room.currentCase}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                            Next: {room.nextCase}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < roomStatus.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            )}
          </Paper>
        </Grid>

        {/* Recent Surgeries */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Recent Surgeries
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ViewIcon />}
                sx={{ borderRadius: 2 }}
              >
                View All
              </Button>
            </Box>
            {recentSurgeries.length === 0 ? (
              <Alert severity="info">
                No recent surgeries found for this facility.
              </Alert>
            ) : (
              <List sx={{ p: 0 }}>
                {recentSurgeries.map((surgery, index) => (
                  <React.Fragment key={surgery.id}>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: getStatusColor(surgery.status) }}>
                          <SurgeryIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {surgery.patientName}
                            </Typography>
                            <Chip
                              label={surgery.status}
                              size="small"
                              sx={{
                                bgcolor: getStatusColor(surgery.status),
                                color: 'white',
                                fontWeight: 500,
                                textTransform: 'capitalize'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              {surgery.surgeryType} • {surgery.room} • {surgery.duration}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              {surgery.team} • {formatDate(surgery.date)}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    {index < recentSurgeries.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Surgery Trends Chart */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 3 }}>
              Surgery Trends (Last 6 Months)
            </Typography>
            {surgeryTrends.length === 0 ? (
              <Alert severity="info">
                No surgery trend data available for this facility.
              </Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={surgeryTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="surgeries"
                    stroke="#708bff"
                    strokeWidth={3}
                    dot={{ fill: '#708bff', strokeWidth: 2, r: 4 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#4caf50"
                    strokeWidth={3}
                    dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Equipment Status */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 3 }}>
              Equipment Status
            </Typography>
            {equipmentStatus.length === 0 ? (
              <Alert severity="info">
                No equipment data available for this facility.
              </Alert>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={equipmentStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {equipmentStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2 }}>
                  {equipmentStatus.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.value}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SurgicalFacilitiesDashboard; 